import MemoryIcon from '@mui/icons-material/Memory';
import { Box, Button, Chip, Stack, Tooltip, Typography, type SxProps } from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { trackRapidRobotTemplateCreateClick } from '../../analytics/bpsEvents';
import { applicationSlice } from '../../redux/ApplicationSlice';
import { ApplicationActions, electronics, type RapidRobotAccent } from '../../utils/constants';
import { getBitsElectronicIcon } from '../../utils/getBitsElectronicIcon';
import { countRequiredElectronicsByType } from '../../utils/requiredElectronicsCounts';
import { Electronic } from '../Dialogs/Robot/RobotTemplate/TemplateCard';

interface RapidRobotCardProps {
  code: string;
  nameKey: string;
  name: string;
  subtitleKey: string;
  subtitle: string;
  description: string;
  descriptionKey: string;
  electronics: Electronic[];
  instructionsUrl?: string;
  image: string;
  accent: RapidRobotAccent;
}

/** Allow two-line labels (e.g. Build Instructions) to wrap and stay centered in narrow card buttons. */
export const rapidRobotCardButtonTextWrapStyles: SxProps<Theme> = {
  minWidth: 0,
  height: 'auto',
  maxHeight: 'none',
  textAlign: 'center',
  whiteSpace: 'normal',
  overflowWrap: 'break-word',
};

const compactButtonStyles = {
  minHeight: 34,
  px: 1,
  py: 0.5,
  borderRadius: '4px',
  ...rapidRobotCardButtonTextWrapStyles,
  '& .MuiTypography-root': {
    fontSize: '0.75rem',
    lineHeight: 1.2,
    whiteSpace: 'normal',
    textAlign: 'center',
    overflowWrap: 'break-word',
    width: '100%',
  },
};

const lightButtonStyles = {
  minHeight: 42,
  px: 2.75,
  py: 1,
  borderRadius: '8px',
  '& .MuiTypography-root': {
    fontSize: '0.9375rem',
    lineHeight: '26px',
  },
};

const getBuildInstructionsButtonStyles = (theme: Theme) => ({
  flex: 1,
  ...compactButtonStyles,
  borderWidth: 1,
  borderColor: 'rapidRobot.cardText',
  color: 'rapidRobot.cardText',
  '&:hover': {
    borderWidth: 1,
  },
  ...theme.applyStyles('light', {
    ...lightButtonStyles,
    borderWidth: 2,
    '&:hover': {
      borderWidth: 2,
      backgroundColor: 'action.hover',
    },
  }),
});

const getDisabledBuildInstructionsButtonStyles = (theme: Theme) => ({
  width: '100%',
  ...compactButtonStyles,
  borderWidth: 1,
  '&.Mui-disabled': {
    borderColor: 'action.disabled',
    color: 'text.disabled',
    backgroundColor: 'action.disabledBackground',
    opacity: 1,
  },
  ...theme.applyStyles('light', {
    ...lightButtonStyles,
    borderWidth: 2,
  }),
});

const getCreateButtonStyles = (theme: Theme) => ({
  flex: 1,
  '&&': {
    ...compactButtonStyles,
    backgroundColor: 'rapidRobot.createButtonBackground',
    color: 'rapidRobot.createButtonText',
    boxShadow: theme.shadows[2],
    '&:hover': {
      backgroundColor: 'rapidRobot.createButtonHoverBackground',
      color: 'rapidRobot.createButtonText',
      boxShadow: theme.shadows[3],
    },
    ...theme.applyStyles('light', {
      ...lightButtonStyles,
      boxShadow: theme.shadows[2],
      '&:hover': {
        backgroundColor: 'rapidRobot.createButtonHoverBackground',
        color: 'rapidRobot.createButtonText',
        boxShadow: theme.shadows[3],
      },
    }),
  },
});

const RapidRobotCard = ({
  code,
  nameKey,
  name,
  subtitleKey,
  subtitle,
  description,
  descriptionKey,
  electronics: requiredElectronics,
  instructionsUrl,
  image,
  accent,
}: RapidRobotCardProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { PROJECT_NAME } = ApplicationActions.Dialogs;

  const requiredElectronicsWithCounts = countRequiredElectronicsByType(requiredElectronics);

  const getElectronicLabel = (electronicType: string): string => {
    switch (electronicType) {
      case electronics.distanceSensor.ELECTRONIC_TYPE:
        return t('electronicExplorer.electronic.distance-sensor', 'Distance Sensor');
      case electronics.highSpeedMotor.ELECTRONIC_TYPE:
        return t('electronicExplorer.electronic.high-speed-motor', 'High Speed Motor+');
      case electronics.highTorqueMotor.ELECTRONIC_TYPE:
        return t('electronicExplorer.electronic.high-torque-motor', 'High Torque Motor+');
      case electronics.beamBreakSensor.ELECTRONIC_TYPE:
        return t('electronicExplorer.electronic.beam-break-sensor', 'Beam Break Sensor');
      case electronics.magneticSwitch.ELECTRONIC_TYPE:
        return t('electronicExplorer.electronic.magnetic-switch', 'Magnetic Switch');
      case electronics.BPEadaptor.ELECTRONIC_TYPE:
        return t('electronicExplorer.electronic.BPE-adaptor', 'BPE Adaptor');
      default:
        return electronicType.replace(/_/g, ' ');
    }
  };

  const handleCreate = () => {
    trackRapidRobotTemplateCreateClick(code);
    dispatch(
      applicationSlice.actions.onOpenDialog({
        ...PROJECT_NAME,
        options: {
          title: 'projectNameDialog.new.title',
          rapidRobotCode: code,
        },
      })
    );
  };

  const handleBuildInstructions = () => {
    if (instructionsUrl) {
      window.open(instructionsUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Stack
      sx={(theme) => {
        const rawAccentColor = theme.palette.rapidRobot[accent];

        return {
          height: '100%',
          minWidth: 0,
          justifyContent: 'space-between',
          borderRadius: '6px',
          padding: { xs: 2, md: 2.5 },
          backgroundColor: 'rapidRobot.cardBackground',
          border: '3px solid',
          borderColor: `rapidRobot.${accent}`,
          boxShadow: `0 0 0 1px ${alpha(rawAccentColor, 0.18)}`,
          transition: 'box-shadow 0.2s',
          overflow: 'hidden',
          '&:hover': {
            boxShadow: `0 0 0 1px ${alpha(rawAccentColor, 0.22)}, ${theme.shadows[1]}`,
          },
        };
      }}
      spacing={2}
    >
      <Box
        sx={{
          width: '100%',
          aspectRatio: '4 / 3',
          borderRadius: '4px',
          overflow: 'hidden',
          backgroundColor: 'spheroWhite.main',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <img
          src={image}
          alt={t('rapidRobots.card.titleWithSubtitle', {
            name: t(nameKey, name),
            subtitle: t(subtitleKey, subtitle),
            defaultValue: '{{name}} - {{subtitle}}',
          })}
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
            borderRadius: 'inherit',
          }}
        />
      </Box>

      <Stack spacing={2} sx={{ flexGrow: 1 }}>
        <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap">
          <Typography
            variant="h4"
            sx={{
              fontWeight: 500,
              color: 'rapidRobot.cardText',
              lineHeight: 1,
            }}
          >
            {t(nameKey, name)}
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'rapidRobot.cardText' }}>
            {t(subtitleKey, subtitle)}
          </Typography>
        </Stack>

        <Typography
          variant="body2"
          sx={{
            color: 'rapidRobot.cardText',
            lineHeight: 1.4,
          }}
        >
          <Trans i18nKey={descriptionKey}>{description}</Trans>
        </Typography>

        <Stack spacing={1}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              color: 'rapidRobot.cardText',
            }}
          >
            <Trans i18nKey="rapidRobots.required-electronics">Required Electronics</Trans>
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
            {Object.entries(requiredElectronicsWithCounts).map(([electronicType, count]) => {
              const ElectronicIcon = getBitsElectronicIcon(electronicType);
              const label = getElectronicLabel(electronicType);

              return (
                <Stack key={electronicType} direction="row" alignItems="center" spacing={0.5}>
                  <Chip
                    icon={
                      ElectronicIcon ? (
                        <Box
                          component={ElectronicIcon}
                          aria-hidden
                          sx={{
                            width: 18,
                            height: 18,
                            display: 'block',
                            color: 'rapidRobot.cardText',
                          }}
                        />
                      ) : (
                        <MemoryIcon />
                      )
                    }
                    label={label}
                    size="small"
                    sx={{
                      fontSize: '0.7rem',
                      height: 24,
                      backgroundColor: 'rapidRobot.cardChipBackground',
                      color: 'rapidRobot.cardText',
                      '& .MuiChip-icon': {
                        fontSize: '0.9rem',
                        color: 'rapidRobot.cardText',
                      },
                    }}
                  />
                  {count > 1 ? (
                    <Typography variant="caption" sx={{ color: 'rapidRobot.cardText', fontWeight: 600 }}>
                      {t('rapidRobots.electronics.quantity', { count, defaultValue: 'x{{count}}' })}
                    </Typography>
                  ) : null}
                </Stack>
              );
            })}
          </Stack>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ paddingTop: 1.5 }}>
        {instructionsUrl ? (
          <Button variant="outlined" onClick={handleBuildInstructions} sx={getBuildInstructionsButtonStyles}>
            <Typography variant="buttonText">
              <Trans i18nKey="rapidRobots.button.build-instructions">Build Instructions</Trans>
            </Typography>
          </Button>
        ) : (
          <Tooltip title={t('rapidRobots.buildInstructions.comingSoon', { defaultValue: 'Build instructions coming soon' })}>
            <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
              <Button variant="outlined" disabled sx={getDisabledBuildInstructionsButtonStyles}>
                <Typography variant="buttonText">
                  <Trans i18nKey="rapidRobots.button.build-instructions">Build Instructions</Trans>
                </Typography>
              </Button>
            </Box>
          </Tooltip>
        )}
        <Button variant="contained" onClick={handleCreate} sx={getCreateButtonStyles}>
          <Typography variant="buttonText">
            <Trans i18nKey="rapidRobots.button.create">Create</Trans>
          </Typography>
        </Button>
      </Stack>
    </Stack>
  );
};

export default RapidRobotCard;
